import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Calendar, Clock, Users, MapPin, Award, 
  BookOpen, Edit, Trash2, Share2, User, MessageSquare, 
  Star, Hourglass, CheckCheck, X, Swords
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getGameSessionById, GameSession, deleteGameSession, Player } from '../lib/game/sessionService';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-hot-toast';

interface UserProfile {
  id: string;
  username: string | null;
  avatar_url?: string | null;
  full_name?: string | null;
}

const SessionDetailsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerProfiles, setPlayerProfiles] = useState<Record<string, UserProfile>>({});
  const [ownerProfile, setOwnerProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    
    const fetchSessionData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await getGameSessionById(sessionId);
        if (!data) {
          setError('Sessão não encontrada');
          return;
        }
        
        setSession(data);
        
        // Buscar o perfil do proprietário da sessão
        if (data.user_id && data.user_id !== user?.id) {
          const { data: ownerData, error: ownerError } = await supabase
            .from('profiles')
            .select('id, username, avatar_url, full_name')
            .eq('id', data.user_id)
            .single();
            
          if (!ownerError && ownerData) {
            setOwnerProfile(ownerData);
          }
        }
        
        // Se houver jogadores com user_id, buscar seus perfis
        if (data.players && data.players.length > 0) {
          const userIds = data.players
            .filter(player => player.user_id)
            .map(player => player.user_id as string);
          
          if (userIds.length > 0) {
            const { data: profiles, error } = await supabase
              .from('profiles')
              .select('id, username, avatar_url, full_name')
              .in('id', userIds);
              
            if (!error && profiles) {
              const profileMap = profiles.reduce((acc, profile) => {
                acc[profile.id] = profile;
                return acc;
              }, {} as Record<string, UserProfile>);
              
              setPlayerProfiles(profileMap);
            }
          }
        }
      } catch (err) {
        console.error('Erro ao carregar detalhes da sessão:', err);
        setError('Não foi possível carregar os detalhes desta sessão. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessionData();
  }, [sessionId]);
  
  const handleEditSession = () => {
    if (!session) return;
    navigate(`/collection/played?edit=${session.id}`);
  };
  
  const handleDeleteSession = async () => {
    if (!session) return;
    
    if (window.confirm('Tem certeza que deseja excluir esta sessão de jogo? Esta ação não pode ser desfeita.')) {
      try {
        const success = await deleteGameSession(session.id);
        if (success) {
          toast.success('Sessão de jogo excluída com sucesso');
          navigate('/collection/played');
        } else {
          toast.error('Erro ao excluir sessão de jogo');
        }
      } catch (err) {
        console.error('Erro ao excluir sessão:', err);
        toast.error('Ocorreu um erro ao excluir a sessão');
      }
    }
  };
  
  const getPlayerName = (player: Player) => {
    if (player.user_id && playerProfiles[player.user_id]) {
      return playerProfiles[player.user_id].full_name || player.name;
    }
    return player.name;
  };
  
  const getPlayerUsername = (player: Player) => {
    if (player.user_id && playerProfiles[player.user_id]) {
      return playerProfiles[player.user_id].username;
    }
    return null;
  };
  
  const getAvatarUrl = (userId: string) => {
    if (playerProfiles[userId]?.avatar_url) {
      return playerProfiles[userId].avatar_url;
    }
    return '';
  };
  
  const canEditSession = () => {
    if (!user || !session) return false;
    return session.user_id === user.id;
  };
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400"></div>
        </div>
      </div>
    );
  }
  
  if (error || !session) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/30 p-6 rounded-lg shadow-sm text-center">
          <X className="h-12 w-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-red-700 dark:text-red-300 mb-2">{error || 'Sessão não encontrada'}</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Não foi possível encontrar a sessão de jogo solicitada</p>
          <Link
            to="/collection/played"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Voltar às Partidas Jogadas
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8 dark:text-gray-100">
      {/* Cabeçalho */}
      <div className="mb-6">
        <Link 
          to="/collection/played" 
          className="inline-flex items-center text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Voltar às Partidas Jogadas
        </Link>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Detalhes da Partida
            </h1>
            
            {session.user_id !== user?.id && ownerProfile && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Registrada por{' '}
                <Link 
                  to={`/profile/${ownerProfile.username}`}
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                >
                  {ownerProfile.full_name || ownerProfile.username}
                </Link>
              </p>
            )}
          </div>
          
          {canEditSession() && (
            <div className="flex space-x-2">
              <button
                onClick={handleEditSession}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </button>
              <button
                onClick={handleDeleteSession}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Excluir
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Card Principal */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-slate-700/50 overflow-hidden mb-8 border border-gray-200 dark:border-gray-700">
        {/* Cabeçalho do Card */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-4">
              {session.game?.image_url ? (
                <img 
                  src={session.game.image_url} 
                  alt={session.game.name}
                  className="h-24 w-24 object-cover rounded-lg shadow-sm" 
                />
              ) : (
                <div className="h-24 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <Swords className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
              )}
            </div>
            
            <div>
              <Link 
                to={`/games/${session.game?.slug || session.game_id}`}
                className="text-xl font-bold text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                {session.game?.name || 'Jogo Desconhecido'}
              </Link>
              
              <div className="mt-1 flex items-center">
                <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                <span className="text-gray-700 dark:text-gray-300">
                  {format(new Date(session.played_at), 'dd/MM/yyyy', { locale: ptBR })}
                </span>
              </div>
              
              <div className="mt-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  session.result === 'vitória' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200' :
                  session.result === 'derrota' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200' :
                  session.result === 'empate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200' :
                  session.result === 'cooperativo' ? 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  <Award className="h-3 w-3 mr-1" />
                  {session.result || 'Resultado não especificado'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Detalhes da Sessão */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informações Básicas */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b dark:border-gray-700 pb-2">Informações da Partida</h2>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-3" />
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Número de Jogadores:</span> {session.player_count}
                </div>
              </div>
              
              {session.duration_minutes && (
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-3" />
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Duração:</span> {session.duration_minutes} minutos
                  </div>
                </div>
              )}
              
              {session.location && (
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-3" />
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Local:</span> {session.location}
                  </div>
                </div>
              )}
              
              {(session.score !== null && session.score !== undefined) && (
                <div className="flex items-start">
                  <Star className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-3 mt-0.5" />
                  <div className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Pontuação:</span> {session.score}
                    {(session.opponent_score !== null && session.opponent_score !== undefined) && 
                      <> vs {session.opponent_score}</>
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Jogadores */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b dark:border-gray-700 pb-2">Jogadores</h2>
            
            {session.players && session.players.length > 0 ? (
              <ul className="space-y-3">
                {session.players.map((player, index) => (
                  <li key={index} className="flex items-center">
                    {player.user_id && getAvatarUrl(player.user_id) ? (
                      <img 
                        src={getAvatarUrl(player.user_id)} 
                        alt={`Avatar de ${player.name}`}
                        className="h-8 w-8 rounded-full mr-3 object-cover" 
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-3">
                        <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    
                    <div className="text-gray-700 dark:text-gray-300">
                      {player.user_id && getPlayerUsername(player) ? (
                        <Link 
                          to={`/profile/${getPlayerUsername(player)}`}
                          className="font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          {getPlayerName(player)}
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-900 dark:text-gray-100">{player.name}</span>
                      )}
                      
                      {player.score !== null && player.score !== undefined && (
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                          ({player.score} pontos)
                        </span>
                      )}
                      
                      {player.is_winner && (
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200`}>
                          Vencedor
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">Nenhum jogador registrado</p>
            )}
          </div>
        </div>
        
        {/* Notas */}
        {session.notes && (
          <div className="px-6 pb-6">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b dark:border-gray-700 pb-2">Notas</h2>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg whitespace-pre-wrap text-gray-700 dark:text-gray-300">
              {session.notes}
            </div>
          </div>
        )}
        
        {/* Informações de Compartilhamento */}
        <div className="dark:bg-gray-750 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Share2 className="h-4 w-4 mr-1" />
            {session.user_id === user?.id ? (
              <span>
                Você registrou esta partida em {format(new Date(session.created_at), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            ) : (
              <div className="flex flex-col">
                <span>
                  Esta sessão foi compartilhada com você por: 
                </span>
                {ownerProfile ? (
                  <Link 
                    to={`/profile/${ownerProfile.username}`} 
                    className="mt-1 font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center"
                  >
                    {ownerProfile.avatar_url ? (
                      <img 
                        src={ownerProfile.avatar_url} 
                        alt={`Avatar de ${ownerProfile.full_name || ownerProfile.username}`}
                        className="h-5 w-5 rounded-full mr-2 object-cover" 
                      />
                    ) : (
                      <User className="h-4 w-4 mr-2 text-indigo-500 dark:text-indigo-400" />
                    )}
                    {ownerProfile.full_name || ownerProfile.username}
                  </Link>
                ) : (
                  <span className="mt-1 italic">Usuário desconhecido</span>
                )}
                <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Registrada em {format(new Date(session.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailsPage; 